import { memo, useEffect, useMemo, useState } from 'react';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Select,
  Text,
} from '@backstage/ui';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { MarkdownContent } from '@backstage/core-components';
import useAsync from 'react-use/esm/useAsync';
import type {
  CheckDefinition,
  CheckFilter,
  CheckResult,
} from '@internal/backstage-plugin-pulse-common';
import { useDryRunCheck } from '../../../../hooks';
import { getCheckApplicability } from '../../../entity-card-checks/check-applicability';
import { selectedKeys, withSelectedFirst } from '../../../select-helpers';
import { SCOPE_FACETS } from '../../../scope-filters/scope-filter-model';

// Every field the scope rules can match on must be fetched, or the client-side
// applicability check reads them as undefined and filters out every entity.
const DRY_RUN_ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.annotations',
  'spec.type',
  ...SCOPE_FACETS,
];

type DryRunCardProps = {
  filterKinds: string[];
  filter?: CheckFilter[];
  exclude?: CheckFilter[];
  getCheck: () => CheckDefinition;
};

/**
 * Dry-run card, isolated so it owns its selection/result state and its entity
 * list. Keeping it separate means typing in the form's text fields never
 * re-renders the entity picker. Entities are loaded on demand for the selected
 * kinds (static once loaded) so the dropdown doesn't churn while open.
 */
export const DryRunCard = memo(function DryRunCard({
  filterKinds,
  filter,
  exclude,
  getCheck,
}: DryRunCardProps) {
  const catalogApi = useApi(catalogApiRef);
  const { dryRun } = useDryRunCheck();

  const [dryRunEntityRefs, setDryRunEntityRefs] = useState<string[]>([]);
  const [dryRunResults, setDryRunResults] = useState<
    { entityRef: string; results: CheckResult[] }[] | null
  >(null);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  // Local flag covering the whole Promise.all so one settled request can't
  // clear the pending state while other entities are still running.
  const [dryRunning, setDryRunning] = useState(false);

  const kindKey = filterKinds.join(',');
  const { value: loadedEntities = [], loading: entitiesLoading } =
    useAsync(async () => {
      if (!filterKinds.length) {
        return [] as Entity[];
      }
      const { items } = await catalogApi.getEntities({
        filter: { kind: filterKinds },
        fields: DRY_RUN_ENTITY_FIELDS,
      });
      return items;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogApi, kindKey]);

  // Only offer entities the check actually applies to (match the include filter
  // and aren't excluded), so excluded kinds/types never appear here.
  const entityOptions = useMemo(
    () =>
      loadedEntities
        .filter(
          entity =>
            getCheckApplicability({ filter, exclude }, entity) === 'applicable',
        )
        .map(entity => stringifyEntityRef(entity))
        .sort((left, right) => left.localeCompare(right))
        .map(ref => ({ value: ref, label: ref })),
    [loadedEntities, filter, exclude],
  );

  // Drop selections that no longer belong to the current kind filter.
  useEffect(() => {
    setDryRunEntityRefs([]);
  }, [kindKey]);

  const orderedEntityOptions = useMemo(
    () => withSelectedFirst(entityOptions, dryRunEntityRefs),
    [dryRunEntityRefs, entityOptions],
  );

  const handleDryRun = async () => {
    setDryRunError(null);
    setDryRunResults(null);
    setDryRunning(true);
    const check = getCheck();
    try {
      const perEntity = await Promise.all(
        dryRunEntityRefs.map(async entityRef => ({
          entityRef,
          results: await dryRun(check, entityRef),
        })),
      );
      setDryRunResults(perEntity);
    } catch (error_) {
      setDryRunError((error_ as Error).message);
    } finally {
      setDryRunning(false);
    }
  };

  const entitiesDescription =
    filterKinds.length === 0
      ? 'Select at least one kind to include or exclude under Scope first'
      : 'Type to filter entities';

  return (
    <Card>
      <CardHeader>
        <Text variant="title-x-small">Dry Run</Text>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="2">
          <Flex direction="column" align="end" gap="1">
            <Select
              label="Entities"
              placeholder="Search entities…"
              options={orderedEntityOptions}
              search
              selectionMode="multiple"
              isDisabled={filterKinds.length === 0}
              loading={{ state: entitiesLoading ? 'loading' : 'idle' }}
              value={dryRunEntityRefs}
              onChange={keys => setDryRunEntityRefs(selectedKeys(keys))}
              description={entitiesDescription}
            />
            <Button
              variant="secondary"
              isPending={dryRunning}
              isDisabled={dryRunEntityRefs.length === 0 || dryRunning}
              onPress={handleDryRun}
            >
              Dry Run
            </Button>
          </Flex>
          {dryRunError ? (
            <Text variant="body-small" color="danger">
              {dryRunError}
            </Text>
          ) : null}
          {!dryRunError &&
            dryRunResults?.map(group => (
              <Flex key={group.entityRef} direction="column" gap="1">
                <Text variant="body-small" weight="bold">
                  {group.entityRef}
                </Text>
                {group.results.length === 0 ? (
                  <Text variant="body-small" color="secondary">
                    No result produced (the entity may have no matching facts).
                  </Text>
                ) : (
                  group.results.map(result => (
                    <Flex
                      key={`${result.checkId}:${result.passed}:${
                        result.message ?? ''
                      }`}
                      direction="column"
                      gap="0"
                    >
                      <Text
                        variant="body-small"
                        color={result.passed ? 'success' : 'danger'}
                      >
                        {result.passed ? 'PASS' : 'FAIL'}
                      </Text>
                      {result.message ? (
                        <MarkdownContent content={result.message} />
                      ) : null}
                    </Flex>
                  ))
                )}
              </Flex>
            ))}
        </Flex>
      </CardBody>
    </Card>
  );
});
